/**
 * Date utility functions for BCE modules
 * Centralizes common date parsing and formatting operations
 */
class DateUtils {
    /**
     * Parse event date from text using multiple formats
     * @param {string} dateText - Text containing date in YYYY-MM-DD, DD.MM.YYYY, or Chinese format
     * @returns {Date|null} Parsed date or null if invalid
     */
    static parseEventDate(dateText) {
        // Try YYYY-MM-DD format (English)
        const isoDateMatch = dateText.match(/(\d{4}-\d{2}-\d{2})/);
        if (isoDateMatch) {
            return new Date(isoDateMatch[1] + 'T00:00:00');
        }
        
        // Try DD.MM.YYYY format (German)
        const germanDateMatch = dateText.match(/(\d{2})\.(\d{2})\.(\d{4})/);
        if (germanDateMatch) {
            const [, day, month, year] = germanDateMatch;
            return new Date(`${year}-${month}-${day}T00:00:00`);
        }
        
        // Try Chinese format: 2025年03月20日
        const chineseDateMatch = dateText.match(/(\d{4})年(\d{2})月(\d{2})日/);
        if (chineseDateMatch) {
            const [, year, month, day] = chineseDateMatch;
            return new Date(`${year}-${month}-${day}T00:00:00`);
        }
        
        return null;
    }

    /**
     * Parse event date range from text (start and end dates)
     * @param {string} dateText - Text containing date range
     * @returns {Object} Object with startDate and endDate properties
     */
    static parseEventDateRange(dateText) {
        // Removed debug logging for production
        
        // Try to find date range patterns
        let startDate = null;
        let endDate = null;
        
        // Chinese format: 2025年03月20日 - 2025年03月21日
        const chineseRangeMatch = dateText.match(/(\d{4})年(\d{2})月(\d{2})日\s*-\s*(\d{4})年(\d{2})月(\d{2})日/);
        if (chineseRangeMatch) {
            const [, startYear, startMonth, startDay, endYear, endMonth, endDay] = chineseRangeMatch;
            startDate = new Date(`${startYear}-${startMonth}-${startDay}T00:00:00`);
            endDate = new Date(`${endYear}-${endMonth}-${endDay}T23:59:59`); // End of day
            return { startDate, endDate };
        }
        
        // German format: 20.03.2025 - 21.03.2025
        const germanRangeMatch = dateText.match(/(\d{2})\.(\d{2})\.(\d{4})\s*-\s*(\d{2})\.(\d{2})\.(\d{4})/);
        if (germanRangeMatch) {
            const [, startDay, startMonth, startYear, endDay, endMonth, endYear] = germanRangeMatch;
            startDate = new Date(`${startYear}-${startMonth}-${startDay}T00:00:00`);
            endDate = new Date(`${endYear}-${endMonth}-${endDay}T23:59:59`); // End of day
            return { startDate, endDate };
        }
        
        // English format: 2025-03-20 - 2025-03-21
        const englishRangeMatch = dateText.match(/(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})/);
        if (englishRangeMatch) {
            const [, startDateStr, endDateStr] = englishRangeMatch;
            startDate = new Date(startDateStr + 'T00:00:00');
            endDate = new Date(endDateStr + 'T23:59:59'); // End of day
            return { startDate, endDate };
        }
        
        // Fallback: Try to parse single date
        startDate = this.parseEventDate(dateText);
        if (startDate) {
            // If only start date found, assume it's a single-day event
            endDate = new Date(startDate.getTime() + (24 * 60 * 60 * 1000) - 1); // End of same day
            return { startDate, endDate };
        }
        
        return { startDate: null, endDate: null };
    }

    /**
     * Calculate days remaining until a date
     * @param {Date} targetDate - Target date
     * @returns {number} Days remaining (negative if past)
     */
    static getDaysUntil(targetDate) {
        const now = new Date().getTime();
        const timeLeft = targetDate.getTime() - now;
        return Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    }

    /**
     * Check if a date is in the past
     * @param {Date} date - Date to check
     * @returns {boolean} True if date is in the past
     */
    static isPast(date) {
        return date.getTime() < Date.now();
    }
}

export default DateUtils;